import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';

function setCarousel(carousel) {
  const orientation = carousel.dataset.orientation || 'horizontal';
  const mainEl = carousel.querySelector('.js-carousel__slider');

  if (!mainEl) return;

  if (orientation === 'vertical') {
    setVerticalCarousel(carousel, mainEl);
  } else {
    setHorizontalCarousel(carousel, mainEl);
  }
}

/**
 * Horizontal mode: a main slider synced 1:1 with a thumbnail strip via
 * Swiper's built-in controller module (a stable 2-node hub-and-spoke), using
 * Swiper's own loop/navigation/autoplay modules directly.
 */
function setHorizontalCarousel(carousel, mainEl) {
  const pagination = carousel.querySelector('.js-carousel__pagination');
  const progressBar = carousel.querySelector('.js-carousel__progressbar');
  const roomsItems = carousel.querySelectorAll('.js-carousel__roomsItem');
  const thumbsEl = carousel.querySelector('.js-carousel__thumbs');

  const autoplay = parseInt(carousel.dataset.autoplay, 10) || false;
  let options = {};

  if (autoplay) {
    options = {
      delay: autoplay,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    };
  }

  const thumbs = thumbsEl
    ? new Swiper(thumbsEl, {
        speed: 1500,
        loop: true,
        spaceBetween: 0,
        slidesPerView: 1,
        parallax: true,
        lazy: {
          loadPrevNext: true,
        },
      })
    : null;

  const images = new Swiper(mainEl, {
    autoplay: options,
    speed: 1500,
    spaceBetween: 0,
    loop: true,
    watchSlidesProgress: true,
    pagination: {
      el: pagination,
      type: 'fraction',
    },
    navigation: {
      nextEl: '.js-carousel__next',
      prevEl: '.js-carousel__prev',
    },
    parallax: true,
    lazy: {
      loadPrevNext: true,
    },
    on: {
      init() {
        if (progressBar) {
          progressBar.style.transition = `width ${autoplay}ms linear`;
          progressBar.style.width = '100%';
        }
        if (roomsItems.length) {
          roomsItems[0].classList.add('active', 'fade-in');
        }
      },
      slideChangeTransitionStart(swiper) {
        if (progressBar) {
          progressBar.style.transition = 'none';
          progressBar.style.width = '0%';
        }

        if (roomsItems.length) {
          roomsItems.forEach((item) => {
            if (item.classList.contains('active')) {
              item.classList.remove('active', 'fade-in');
              item.classList.add('fade-out');
              setTimeout(() => {
                item.classList.remove('fade-out');
              }, 500);
            }
          });
          setTimeout(() => {
            roomsItems[swiper.realIndex].classList.add('fade-in');
            setTimeout(() => {
              roomsItems[swiper.realIndex].classList.add('active');
            }, 100);
          }, 500);
        }
      },
      slideChangeTransitionEnd() {
        if (progressBar) {
          progressBar.style.transition = `width ${autoplay}ms linear`;
          progressBar.style.width = '100%';
        }
      },
    },
  });

  if (thumbs) {
    images.controller.control = thumbs;
    thumbs.controller.control = images;
  }
}

/**
 * Vertical mode: 3 side-by-side columns (right/middle/left), each showing a
 * different image at any given time (see BlocksCarousel::override() for the
 * n / n-1 / n-2 offset). Swiper's loop:true has a reproducible bug in this
 * exact setup — confirmed independent of every other module/script on the
 * page — where a single slideNext()/slideToLoop() call right at the wrap
 * boundary fires two internal transitions back to back, visibly skipping a
 * slide and freezing the previous frame for a beat ("rewind" look).
 *
 * Loop mode's automatic slide-duplicating/repositioning is what's buggy, so
 * it's sidestepped entirely here: each column's slide array is rendered 3
 * times over (BlocksCarousel::override()) and driven with loop:false + plain
 * slideTo(). All 3 columns are stepped by the exact same delta at the same
 * time, so they never need runtime syncing — they simply stay in lockstep.
 * Whenever a column's position drifts out of the middle copy, it's silently
 * rebased back into it with a 0-speed jump: position p and p +/- $count show
 * the same image, so the rebase is invisible.
 */
function setVerticalCarousel(carousel, mainEl) {
  const pagination = carousel.querySelector('.js-carousel__pagination');
  const progressBar = carousel.querySelector('.js-carousel__progressbar');
  const autoplay = parseInt(carousel.dataset.autoplay, 10) || false;
  const count = parseInt(carousel.dataset.slidesCount, 10) || 0;

  if (!count) return;

  const columnEls = [mainEl, carousel.querySelector('.js-carousel__middle'), carousel.querySelector('.js-carousel__left')].filter(
    Boolean
  );

  const columns = columnEls.map(
    (el, i) =>
      new Swiper(el, {
        speed: 1500,
        spaceBetween: 0,
        slidesPerView: 1,
        loop: false,
        initialSlide: count,
        parallax: true,
        lazy: {
          loadPrevNext: true,
        },
        // Only the main (right) column is interactive; middle/left are pure
        // mirrors, always driven by the same goTo() calls as the main one.
        allowTouchMove: i === 0,
      })
  );

  const [images] = columns;

  const logicalIndex = () => ((images.activeIndex % count) + count) % count;

  const renderPagination = () => {
    if (!pagination) return;
    pagination.innerHTML =
      `<span class="swiper-pagination-current">${logicalIndex() + 1}</span>` +
      ' / ' +
      `<span class="swiper-pagination-total">${count}</span>`;
  };

  const rebase = () => {
    columns.forEach((swiper) => {
      if (swiper.activeIndex >= count * 2) {
        swiper.slideTo(swiper.activeIndex - count, 0, false);
      } else if (swiper.activeIndex < count) {
        swiper.slideTo(swiper.activeIndex + count, 0, false);
      }
    });
  };

  const goTo = (delta) => {
    columns.forEach((swiper) => swiper.slideTo(swiper.activeIndex + delta, swiper.params.speed));
  };

  if (progressBar) {
    progressBar.style.transition = `width ${autoplay}ms linear`;
    progressBar.style.width = '100%';
  }
  renderPagination();

  let autoplayTimer = null;

  // Scheduled from slideChangeTransitionEnd (not a fixed-interval clock), so
  // the next auto-advance always happens exactly `autoplay` ms after the
  // previous transition actually finished — matching how the progress bar
  // fill (also started at transitionEnd) is timed, and matching how Swiper's
  // own native autoplay counts its delay after the transition ends.
  const scheduleAutoplay = () => {
    if (!autoplay || count <= 1) return;
    clearTimeout(autoplayTimer);
    autoplayTimer = setTimeout(() => goTo(1), autoplay);
  };

  images.on('slideChangeTransitionStart', () => {
    renderPagination();

    if (progressBar) {
      progressBar.style.transition = 'none';
      progressBar.style.width = '0%';
    }
  });

  images.on('slideChangeTransitionEnd', () => {
    if (progressBar) {
      progressBar.style.transition = `width ${autoplay}ms linear`;
      progressBar.style.width = '100%';
    }

    rebase();
    scheduleAutoplay();
  });

  const nextBtn = carousel.querySelector('.js-carousel__next');
  const prevBtn = carousel.querySelector('.js-carousel__prev');
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(1));
  if (prevBtn) prevBtn.addEventListener('click', () => goTo(-1));

  if (autoplay && count > 1) {
    scheduleAutoplay();
  }
}

document.querySelectorAll('.js-carousel').forEach(setCarousel);
